import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { supabase } from '../../supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

// 👇 separa bucket y carpeta
const BUCKET = 'calendariobellavista'; // nombre real del bucket en Supabase
const FOLDER = 'datos2';            // subcarpeta dentro del bucket

const Container = styled.div`
  max-width: 70%;
  text-align: left;
  background-color: #f9f9f9;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
  margin: 0 auto 0px auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    max-width: 94%;
  }
`;

const ImageContainer = styled.div`
  padding: 30px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DeleteButton = styled.div`
  position: absolute;
  top: 10px;
  right: -5px;
  color: #ff0000;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  user-select: none;

  &:hover {
    background-color: #ff000045;
  }
`;

const DownloadButton = styled.button`
  margin: 15px;
  padding: 5px 13px;
  border: none;
  background-color: #a0f000;
  color: #000000;
  cursor: pointer;
  border-radius: 5px;
  transition: background-color 0.2s ease-in-out;
  font-size: 15px;

  &:hover {
    background-color: #c0ff41;
  }

  ${props => props.disabled && css`
    background-color: #ccc;
    cursor: not-allowed;
  `}
`;

const Message = styled.p`
  color: #28a745;
`;

const ErrorMessage = styled.p`
  color: #dc3545;
`;

const UploaderContainer = styled.div`
  max-width: 100%;
  text-align: center;
  margin: 20px auto;
  padding: 20px;
  font-size: 13px;
`;

const TitleInput = styled.input`
  margin: 10px 0;
  padding: 5px;
  width: 80%;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const Title = styled.h3`
  font-size: 20px;
  margin-bottom: 0px;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 16px;
    text-align: center;
  }
`;

const CalendarioRioGrande = () => {
  const [file, setFile] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [titles, setTitles] = useState({});
  const [currentTitle, setCurrentTitle] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchImages();
    checkUserRole();
  }, []);

  const checkUserRole = () => {
    const userRole = localStorage.getItem('rolUsuario');
    if (userRole === 'admin') {
      setIsAdmin(true);
    }
  };

  const fetchImages = async () => {
    try {
      const { data, error } = await supabase
        .storage
        .from(BUCKET)
        .list(FOLDER, { limit: 100 }); // listar dentro de la carpeta

      if (error) throw error;

      const files = await Promise.all(
        data.map(async (file) => {
          const { data: urlData } = supabase
            .storage
            .from(BUCKET)
            .getPublicUrl(`${FOLDER}/${file.name}`);

          return { url: urlData.publicUrl, name: file.name };
        })
      );

      setImages(files);

      const titlesFromStorage = {};
      files.forEach((file) => {
        titlesFromStorage[file.url] = file.name.split('.')[0];
      });
      setTitles(titlesFromStorage);
    } catch (err) {
      console.error(err);
      setError('Error al cargar imágenes.');
    }
  };

  const handleFileChange = (event) => {
    const newFile = event.target.files[0];
    if (newFile) {
      setFile(newFile);
      setMessage('');
      setError('');
    }
  };

  const handleTitleChange = (event) => {
    setCurrentTitle(event.target.value);
  };

  const uploadFile = async () => {
    if (!file) {
      setError('Por favor, seleccione un archivo primero.');
      return;
    }
    if (!currentTitle) {
      setError('Por favor, ingrese un título para el archivo.');
      return;
    }

    setLoading(true);
    const fileName = `${currentTitle}.${file.name.split('.').pop()}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(`${FOLDER}/${fileName}`, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase
        .storage
        .from(BUCKET)
        .getPublicUrl(`${FOLDER}/${fileName}`);

      const url = urlData.publicUrl;

      setImages((prev) => [...prev, { url, name: fileName }]);
      setTitles((prev) => ({ ...prev, [url]: currentTitle }));
      setMessage('Archivo subido con éxito.');
      setCurrentTitle('');
    } catch (err) {
      console.error(err);
      setError('Error al subir el archivo.');
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (name) => {
    try {
      const { error: deleteError } = await supabase.storage
        .from(BUCKET)
        .remove([`${FOLDER}/${name}`]);

      if (deleteError) throw deleteError;

      setImages(images.filter((image) => image.name !== name));

      const updatedTitles = { ...titles };
      const urlToDelete = Object.keys(titles).find((key) =>
        key.includes(name)
      );
      if (urlToDelete) {
        delete updatedTitles[urlToDelete];
      }

      setTitles(updatedTitles);
      setMessage('Imagen eliminada con éxito.');
    } catch (err) {
      console.error(err);
      setError('Error al eliminar la imagen.');
    }
  };

  return (
    <>
      <Container>
        {images.map(({ url, name }, index) => {
          const extension = url.split('.').pop().split('?')[0].toLowerCase();

          return (
            <ImageContainer key={index}>
              <Title>{titles[url]}</Title>
              {extension.match(/(jpg|jpeg|png|gif)$/i) ? (
                <StyledImage src={url} alt="Uploaded" />
              ) : extension === 'pdf' ? (
                <DownloadButton as="a" href={url} download target="_blank" rel="noopener noreferrer">
                  Descargar PDF
                </DownloadButton>
              ) : extension.match(/(mp4|mov)$/i) ? (
                <video src={url} controls style={{ width: '100%' }}></video>
              ) : (
                <p>Archivo no soportado para visualización</p>
              )}
              {isAdmin && (
                <DeleteButton onClick={() => deleteImage(name)}>
                  <FontAwesomeIcon icon={faTimes} />
                </DeleteButton>
              )}
            </ImageContainer>
          );
        })}
      </Container>

      {isAdmin && (
        <UploaderContainer>
          <input type="file" onChange={handleFileChange} disabled={loading} />
          <TitleInput
            type="text"
            placeholder="Ingrese un título para el archivo"
            value={currentTitle}
            onChange={handleTitleChange}
            disabled={loading}
          />
          <DownloadButton onClick={uploadFile} disabled={loading}>
            {loading ? 'Subiendo...' : 'Subir Archivo'}
          </DownloadButton>
          {message && <Message>{message}</Message>}
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </UploaderContainer>
      )}
    </>
  );
};

export default CalendarioRioGrande;
